import { createSearchParams } from 'react-router-dom'

import createMemo from 'modules/core/hooks/createMemo'
import countryList from 'modules/core/lib/countryList'
import {
  dateBackendToUI,
  formatBackendDateToMonthDay,
  formatBackendDateToYear,
} from 'modules/core/lib/dates'
import formatCurrency from 'modules/core/lib/formatCurrency'
import i18n from 'modules/core/lib/i18n'
import { Navigation } from 'modules/core/lib/navigation/context'
import { NavigationPaths } from 'modules/core/lib/navigation/context'
import users from 'modules/core/store/collections/users'
import type Company from 'modules/core/store/models/Company'

import { additionalCompensationType } from 'modules/bookkeepers/lib/dynamicTranslations'
import CompensationChange from 'modules/bookkeepers/store/models/CompensationChange'
import { translatedWeekdays } from 'modules/contracts/lib/weekdays'
import type Document from 'modules/documents/store/models/Document'
import { COMMON_TABS, TABS } from 'modules/payroll_bundle/lib/constants'

import datevCountryOfBirthMappings from './datevCountryOfBirthMappings.json'
import denominationMappings from './denomination.json'
import highestLevelOfEducationMappings from './highestLevelOfEducation.json'
import highestLevelOfProfessionalTrainingMappings from './highestLevelOfProfessionalTraining.json'
import personnelLeasingMappings from './personnelLeasing.json'
import spousesDenominationMappings from './spousesDenomination.json'
import type { FieldObject, FieldValue, IncidenceChangeValue } from './types'

type mappingsInfo = {
  [key: string]: string
}

export const documentAuthor = (document: Document) => {
  return users.get(document.attributes.author_id)
}

export const dateModifier = ({ fieldValue: date }: FieldObject) => {
  if (typeof date === 'string') {
    return dateBackendToUI(date)
  }

  return null
}

export const countryModifier = ({ fieldValue: countryCode }: FieldObject) => {
  const countries = countryList[i18n.locale as keyof typeof countryList] || countryList.en

  if (typeof countryCode === 'string') {
    return countries.find((country) => country.value === countryCode)?.label ?? null
  }

  return null
}

export const countryOfBirthModifier = ({ fieldValue: country_of_birth }: FieldObject) => {
  const countries = countryList[i18n.locale as keyof typeof countryList] || countryList.en
  const countriesOfBirth: mappingsInfo = datevCountryOfBirthMappings

  if (typeof country_of_birth === 'string') {
    const countryOfBirthCode = countriesOfBirth[country_of_birth]
    return countries.find((country) => country.value === countryOfBirthCode)?.label ?? null
  }

  return null
}

export const personnelLeasingModifier = ({ fieldValue: personnel_leasing }: FieldObject) => {
  const personnelLeasings: mappingsInfo = personnelLeasingMappings

  if (typeof personnel_leasing === 'string') {
    return personnelLeasings[personnel_leasing]
  }

  return null
}

export const highestLevelOfEducationModifier = ({
  fieldValue: highest_level_of_education,
}: FieldObject) => {
  const educationLevels: mappingsInfo = highestLevelOfEducationMappings

  if (typeof highest_level_of_education === 'string') {
    return educationLevels[highest_level_of_education]
  }

  return null
}

export const highestLevelOfProfessionalTrainingModifier = ({
  fieldValue: highest_level_of_professional_training,
}: FieldObject) => {
  const professionalTrainingLevels: mappingsInfo = highestLevelOfProfessionalTrainingMappings

  if (typeof highest_level_of_professional_training === 'string') {
    return professionalTrainingLevels[highest_level_of_professional_training]
  }

  return null
}

export const denominationModifier = ({ fieldValue: denomination }: FieldObject) => {
  const denominations: mappingsInfo = denominationMappings

  if (typeof denomination === 'string') {
    return denominations[denomination]
  }

  return null
}

export const spousesDenominationModifier = ({ fieldValue: spouses_denomination }: FieldObject) => {
  const spousesDenominations: mappingsInfo = spousesDenominationMappings

  if (typeof spouses_denomination === 'string') {
    return spousesDenominations[spouses_denomination]
  }

  return null
}

export const weekdaysModifier = ({ fieldValue: weekdays }: FieldObject) => {
  if (typeof weekdays === 'string') {
    const weekdaysList = weekdays.split(',')

    return weekdaysList.map((day: string) => translatedWeekdays(day)).join(', ')
  }

  return null
}

export const genderModifier = ({ fieldValue: gender }: FieldObject) => {
  switch (gender) {
    case 'male':
      return i18n.t('common.male')
    case 'female':
      return i18n.t('common.female')
    default:
      return null
  }
}

export const frequencyModifier = ({ fieldValue: frequency }: FieldObject) => {
  switch (frequency) {
    case 'daily':
      return i18n.t('common.daily')
    case 'weekly':
      return i18n.t('common.weekly')
    case 'monthly':
      return i18n.t('common.monthly')
    case 'yearly':
      return i18n.t('common.yearly')
    default:
      return null
  }
}

const salaryFrequencyModifier = ({ fieldValue: frequency }: Partial<FieldObject>) => {
  const frequencies: Record<string, string> = {
    daily: i18n.t('bookkeepers.incidence.contract_version.details.salary_frequency.day'),
    weekly: i18n.t('bookkeepers.incidence.contract_version.details.salary_frequency.week'),
    monthly: i18n.t('bookkeepers.incidence.contract_version.details.salary_frequency.month'),
    yearly: i18n.t('bookkeepers.incidence.contract_version.details.salary_frequency.year'),
    hourly: i18n.t('bookkeepers.incidence.contract_version.details.salary_frequency.hour'),
  }

  if (typeof frequency === 'string') {
    return frequencies[frequency] ?? frequency
  }

  return null
}

export const workingHoursfrequencyModifier = ({ fieldValue: frequency }: FieldObject) => {
  const frequencies: Record<string, string> = {
    day: i18n.t('bookkeepers.incidence.contract_version.details.working_hours_frequency.day'),
    week: i18n.t('bookkeepers.incidence.contract_version.details.working_hours_frequency.week'),
    month: i18n.t('bookkeepers.incidence.contract_version.details.working_hours_frequency.month'),
    year: i18n.t('bookkeepers.incidence.contract_version.details.working_hours_frequency.year'),
  }

  if (typeof frequency === 'string') {
    return frequencies[frequency]
  }

  return null
}

export const salaryCurrencyFrequencyModifier = ({
  fieldValue: value,
  extraValue: frequency,
  company,
  legalEntityId,
}: FieldObject) => {
  if (typeof value === 'number' && typeof frequency === 'string') {
    const formattedAmount = currencyModifier({ fieldValue: value, company, legalEntityId })
    const formattedFrequency = salaryFrequencyModifier({ fieldValue: frequency })

    if (formattedAmount) {
      return formattedFrequency ? `${formattedAmount} ${formattedFrequency}` : formattedAmount
    }
  }

  return null
}

export const booleanModifier = ({ fieldValue: option }: FieldObject) => {
  if (typeof option === 'boolean') {
    return option
      ? i18n.t('bookkeepers.incidence.boolean.true')
      : i18n.t('bookkeepers.incidence.boolean.false')
  }

  return null
}

export const centsModifier = ({ fieldValue: cents }: FieldObject) => {
  if (typeof cents === 'number') {
    return (cents / 100).toString()
  }

  return null
}

export const currencyModifier = ({ fieldValue: value, company, legalEntityId }: FieldObject) => {
  if (typeof value === 'number') {
    return formatCurrency(value, '', company, legalEntityId)
  }

  return null
}
export const centsToString = (
  amount: number,
  unit: string,
  company: Company,
  legalEntityId?: number
) => {
  const CENT = 100

  switch (unit) {
    case 'money':
      return currencyModifier({ fieldValue: amount, company, legalEntityId })
    case 'units':
      return i18n.tp('compensation.unit.units', { count: Number(amount) / CENT })
    default:
      return null
  }
}

export const hasValidValue = (value: FieldValue) => {
  return value !== null && value !== undefined && value !== ''
}

export const workingHoursModifier = ({
  fieldValue: value,
  extraValue: unit,
  company,
}: FieldObject) => {
  if (typeof unit === 'string' && typeof value === 'number') {
    const modifiedValue = centsModifier({ fieldValue: value, company })
    const params = {
      value: modifiedValue,
    }

    const frequencies: Record<string, string> = {
      day: i18n.t(
        'bookkeepers.incidence.contract_version.details.working_hours_frequency.day',
        params
      ),
      week: i18n.t(
        'bookkeepers.incidence.contract_version.details.working_hours_frequency.week',
        params
      ),
      month: i18n.t(
        'bookkeepers.incidence.contract_version.details.working_hours_frequency.month',
        params
      ),
      year: i18n.t(
        'bookkeepers.incidence.contract_version.details.working_hours_frequency.year',
        params
      ),
    }

    return frequencies[unit] ?? `${modifiedValue} ${unit}`
  }

  return null
}

export const residenceModifier = ({ fieldValue: option }: FieldObject) => {
  return option
    ? i18n.t('employee_form.tax_residence.resident')
    : i18n.t('employee_form.tax_residence.not_resident')
}

export const displayCotizationGroup = ({ fieldValue: id }: FieldObject) =>
  id ? translatedCotizationGroup(id.toString()) : null

/**
 * Compensation related modifiers
 */
export const compensationTypeModifier = (
  company: Company,
  legalEntityId?: number,
  changeValue?: IncidenceChangeValue
) => {
  if (!changeValue?.compensation_type) return null

  const { compensation_type, amount, unit } = changeValue

  return (
    additionalCompensationType(
      `${compensation_type ?? ''}`,
      centsToString(Number(amount), unit?.toString() || '', company, legalEntityId)
    ) ?? null
  )
}

export const compensationRecurrenceModifier = (
  _company: Company,
  _legalEntityId?: number,
  changeValue?: IncidenceChangeValue
) => {
  if (!changeValue?.starts_on && !changeValue?.first_payment_on) return null

  const { recurrence, recurrence_count, sync_with_supplements, first_payment_on, starts_on } =
    changeValue
  const firstPayment = (sync_with_supplements ? starts_on : first_payment_on) ?? ''

  return i18n.t('bookkeepers.additional_compensation.recurrence', {
    recurrence: CompensationChange.translateRecurrence(
      `${recurrence ?? ''}`,
      Number(recurrence_count),
      !!sync_with_supplements
    ),
    first_payment_on: `${formatBackendDateToMonthDay(`${firstPayment}`)}, ${formatBackendDateToYear(
      `${firstPayment}`
    )}`,
  })
}

export const buildTabs = createMemo(
  (
    incidenceId: number,
    isBookkeeper: boolean,
    navigation: Navigation<NavigationPaths>,
    incidenceType: string | undefined,
    taskSummary?: string,
    hasInHousePayroll?: boolean,
    hasCompensations?: boolean,
    notesSummary?: string,
    messagesSummary?: string,
    isDataCollection?: boolean
  ) => {
    const incidenceTypeParams = incidenceType && createSearchParams({ incidenceType })
    const tabsList = COMMON_TABS(
      incidenceId,
      isBookkeeper,
      navigation,
      incidenceTypeParams,
      hasInHousePayroll,
      messagesSummary,
      isDataCollection
    )

    const buildUrl = (name: string) => {
      const paths = {
        [TABS.tasks]: navigation.bookkeepers.incidences.idTasks,
        [TABS.notes]: navigation.bookkeepers.incidences.idNotes,
      }

      const fullPath = paths[name]?.fullPath({ id: incidenceId })

      if (!fullPath) {
        throw new Error('Invalid bookkeeper incidence detail tab id')
      }

      if (incidenceType) {
        return `${fullPath}?${incidenceTypeParams}`
      }

      return fullPath
    }

    if (isBookkeeper) {
      tabsList.push(
        {
          id: TABS.tasks,
          url: buildUrl(TABS.tasks),
          name: i18n.t('bookkeepers.detail_tabs.tasks'),
          additionalInformation: taskSummary,
        },
        {
          id: TABS.notes,
          url: buildUrl(TABS.notes),
          name: i18n.t('bookkeepers.detail_tabs.notes'),
          additionalInformation: notesSummary,
        }
      )
    }

    return tabsList
  }
)

export const translatedCotizationGroup = (id: string): string | null => {
  const translationsMap: Record<string, string> = {
    1: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.1'),
    2: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.2'),
    3: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.3'),
    4: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.4'),
    5: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.5'),
    6: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.6'),
    7: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.7'),
    8: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.8'),
    9: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.9'),
    10: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.10'),
    11: i18n.t('contract_versions.forms.contract_extra_information.es_cotization_group_options.11'),
  }

  return translationsMap[id] ?? null
}
